/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdInput.scss';

import React, {
    ChangeEventHandler,
    CSSProperties,
    DetailedHTMLProps,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    MouseEventHandler,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import classNames from "classnames";
import {ElementPosition, ErrorType, OrnamentShape} from '../util/interfaces';
import AwesomeIcon from '../icon/awesomeIcon';
import {directTimeout} from '../util/componentUtil';
import {stopPropagation} from "../util/controlUtil";
import NumberString from "../util/numberString";

interface Props {
    onChange: ChangeEventHandler;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown: KeyboardEventHandler;
    onKeyUp?: KeyboardEventHandler;
    onClearInput?: MouseEventHandler;
    value: string | number | NumberString;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    // inputProps?: InputHTMLAttributes<any> & TextareaHTMLAttributes<any>;
    inputProps?: Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, HTMLInputElement | HTMLTextAreaElement>, 'crossOrigin'>;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id: string;
    fullWidth?: boolean;
    area?: boolean;
    noResize?: boolean;
}

export default function DvrdInput(props: Props) {
    const {
        area, id, label, labelClassName, errorClassName, inputClassName, className, ornamentClassName, inputProps,
        disabled, onBlur, value, onChange, onFocus, autoSelect, autoFocus, onClearInput, noResize, onKeyDown, fullWidth,
        ornaments, error, onKeyUp
    } = props;
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const [active, setActive] = useState(props.autoFocus ?? false);
    const hasOrnaments = useMemo(() => !(!ornaments || (Array.isArray(ornaments) && !ornaments.length)),
        [ornaments]);

    function onClickElement(evt: React.MouseEvent) {
        stopPropagation(evt);
        focusInput();
    }

    function onFocusInput(evt: React.FocusEvent) {
        if (!disabled) {
            setActive(true);
            const input = inputRef.current;
            if (autoSelect && input) {
                input.selectionStart = 0;
                input.selectionEnd = value.toString().length;
            }
        }
        onFocus?.(evt);
    }

    function onBlurInput(evt: React.FocusEvent) {
        if (!disabled) setActive(false);
        onBlur?.(evt);
    }

    function focusInput(retry: boolean = false) {
        if (inputRef.current) inputRef.current.focus();
        else if (retry) directTimeout(focusInput);
    }

    function getContentStyle(): CSSProperties {
        const style: CSSProperties = {gridTemplateColumns: '1fr'};
        if (!hasOrnaments) style.gridTemplateColumns = '1fr';
        let hasLeft: boolean = false, hasRight: boolean = onClearInput !== undefined;
        if (Array.isArray(ornaments))
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT) hasLeft = true;
                else if (ornament.placement === ElementPosition.RIGHT) hasRight = true;
                if (hasLeft && hasRight) break;
            }
        else if (ornaments) {
            hasLeft = !ornaments.placement || ornaments.placement === ElementPosition.LEFT;
            hasRight = ornaments.placement === ElementPosition.RIGHT;
        }
        let columns: string = '1fr';
        let paddingLeft: string = '';
        let paddingRight: string = '';
        if (hasRight) {
            columns = `${columns} fit-content(100%)`
            paddingRight = '.75rem';
        }
        if (hasLeft) {
            columns = `fit-content(100%) ${columns}`;
            paddingLeft = '.75rem';
        }
        style.gridTemplateColumns = columns;
        style.paddingRight = paddingRight;
        style.paddingLeft = paddingLeft;
        return style;
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className={classNames('dvrd-input-label', labelClassName)} onClick={onClickElement}>{label}</label>
        );
    }

    function renderInput() {
        const elementProps = {
            ...inputProps, value: value.toString(), onChange, onFocus: onFocusInput, onBlur: onBlurInput, onKeyDown,
            disabled, className: classNames('dvrd-input', inputClassName, noResize && 'no-resize'), id: `${id}-input`,
            ref: inputRef, onKeyUp,
        };
        if (area) return <textarea {...elementProps}/>;
        return <input {...elementProps}/>
    }

    function renderLeftOrnament() {
        if (!hasOrnaments) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (!ornament.placement || ornament.placement === ElementPosition.LEFT)
                    return renderOrnament(ornament);
            }
        } else if (ornaments && (!ornaments.placement || ornaments.placement === ElementPosition.LEFT))
            return renderOrnament(ornaments);
        return null;
    }

    function renderRightOrnament() {
        if (onClearInput && value) return renderOrnament({
            element: <AwesomeIcon onClick={onClearInput} name='backspace' className='clear-btn'/>,
            placement: ElementPosition.RIGHT
        });
        if (!hasOrnaments) return null;
        if (Array.isArray(ornaments)) {
            for (const ornament of ornaments) {
                if (ornament.placement === ElementPosition.RIGHT)
                    return renderOrnament(ornament);
            }
        } else if (ornaments && ornaments.placement === ElementPosition.RIGHT)
            return renderOrnament(ornaments);
        return null;
    }

    function renderOrnament(ornament: OrnamentShape) {
        if (typeof ornament.element === 'string')
            return <span className={classNames('dvrd-input-ornament', ornamentClassName)}
                         onClick={onClickElement}>{ornament.element}</span>;
        else return React.cloneElement(ornament.element, {
            className: classNames((ornament.element.props as any).className, ornamentClassName),
            onClick: (evt: React.MouseEvent) => {
                onClickElement(evt);
                if (React.isValidElement(ornament.element)) {
                    const element: React.ReactElement<any> = ornament.element;
                    element.props.onClick?.(evt);
                }
            }
        } as any);
    }

    useEffect(() => {
        if (autoFocus) focusInput(true);
    }, [autoFocus]);

    return (
        <div onClick={onClickElement} id={id}
             className={classNames('dvrd-input-container', disabled && 'disabled', !!error && 'error',
                 active && 'active', fullWidth && 'full', className)}>
            {renderLabel()}
            <div className='content-container' style={getContentStyle()}>
                {renderLeftOrnament()}
                {renderInput()}
                {renderRightOrnament()}
            </div>
            <span className={classNames('dvrd-input-error', errorClassName)}>{error}</span>
        </div>
    )
}